/**
 * @file   src\Pages\AddVideoAdvertisement.tsx
 * @brief  AddVideoAdvertisement page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, useEffect, Container, Badge, Button, Breadcrumb, Row, Col, useOutletContext, useParams, useNavigate, Form, useRef } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWTextarea from '../components/CWTextarea';
import { pageURLs } from '../utils/pageUrls';
import * as Yup from 'yup'
import { MessageToaster } from '../utils/toastUtil';
import { cwoaddadvertisementSchema } from '../validations/cwoaddadvertisementSchema';
import { NAME_MAX_LENGTH, WASH_DETAIL_MAX_LENGTH, CUSTOM_ADVERTISEMENT_IMAGE_SIZE} from '../utils/constants';
import { AppContextType } from '../interfaces/generalInterface';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Videoimgicon from '../assets/images/video-icon.svg';
import GenerateLink from '../components/GenerateLink';
import CarWashStatus from '../components/CarWashStatus';
import Fileimgicon from '../assets/images/photo-icon.svg';
import { userType, AdvertisementType } from '../utils/enums';
import { validateImageTypes } from '../utils/helper';
import SmallLoader from '../components/SmallLoader';
import { CommonMessages } from "../messages/validationMessages";
import axios from '../config/axiosConfig';
import { CommonApi, AdvertisementApi, LocationApi, CompanyApi } from "../utils/apiUrls";
import CloseIcon from '../assets/images/icons/close';
import { LocationItem, Option } from '../interfaces/subscriptionInterface';

const AddVideoAdvertisement = (props: any) => {

    const [loading, setLoading] = useState(false);
    const param = useParams();
    const company_id = param.id ? parseInt(param.id) : 0;
    const advertisement_id = param.ad_id ? parseInt(param.ad_id) : 0;

    // State to handle locations given in dropdown
    const [locations, setLocations] = useState<Option[]>([]);
    // Function to get Locations
    const getCompanyLocations = async () => {
        try {
            setLoading(true);
            // Fetch the company details based on userType
            const { data } = (props.userType !== userType["CARWASH-OWNER"])
                ? await axios.get(CompanyApi.GET_COMPANY + company_id)
                : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);
    
            if (data?.error === 0) {
                // Fetch locations under the company
                const locationResponse = (props.userType !== userType["CARWASH-OWNER"])
                    ? await axios.get(LocationApi.GET_COMPANY_LOCATIONS + data.data.carWashGUID)
                    : await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS);
    
                const locationData = locationResponse.data;
    
                if (locationData.error === 0) {
                    const locationList: LocationItem[] = locationData?.data;
    
                    // Transform the array
                    const transformedArray: Option[] = locationList.map(item => ({
                        label: item.locationName,
                        value: item.locationId,
                    }));
    
                    setLocations(transformedArray);
                } else {
                    // Show error message
                    toaster.toastError(locationData.message);
                }
            } else {
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            console.log(error);
            toaster.toastError("Some error occured!");
        }
        finally
        {
            setLoading(false);
        }
    };

    // To get list of bays in location
    const getadvertisement = async () => {
        if(advertisement_id)
        {
            try {
                setLoading(true);
                const { data } = props.userType !== userType['CARWASH-OWNER'] ? await axios.get(AdvertisementApi.GET_ADVERTISEMENT_DETAILS + advertisement_id) : await axios.get(AdvertisementApi.CARWASH_OWNER_GET_ADVERTISEMENT_DETAILS+ advertisement_id);
                const advertisement_data = data?.data;
                setFormData({
                    advertisement_id: advertisement_id,
                    advertisementtype: advertisement_data.advertisementType,
                    assignedlocation: advertisement_data.locationId,
                    advertisementname: advertisement_data.advertisementName,
                    startDate:  advertisement_data.validFrom,
                    endDate: advertisement_data.validTo,
                    file: advertisement_data.advertisementUrl,
                    advertisement_des: advertisement_data.advertisementDesc,
                });
                setStartDate(advertisement_data.validFrom);
                setEndDate(advertisement_data.validTo);
                if(advertisement_data.advertisementType === 2)
                {
                    setAdvertisementImageName(advertisement_data.advertisementUrl);
                    setAdvertisementImageUrl(advertisement_data.advertisementUrl);
                    setCurrentfilevalue(advertisement_data.advertisementUrl);
                }
                else if(advertisement_data.advertisementType === 3)
                {
                    setAdvertisementVideoName(advertisement_data.advertisementUrl);
                    setAdvertisementVideoUrl(advertisement_data.advertisementUrl);
                    setCurrentfilevalue(advertisement_data.advertisementUrl);
                }
    
            } catch (error: any) {
                throw new Error(error.response?.data?.message || error.message);
            }
            finally{
                setLoading(false);
            }
        }
    }
    
    // Create the adtype state using the enum
    const [adtype] = useState(
        Object.entries(AdvertisementType)
            .filter(([key]) => isNaN(Number(key))) // Exclude numeric keys from enum
            .map(([label, value]) => ({ label, value: Number(value) }))
    );

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    const navigate = useNavigate()

    const toaster = new MessageToaster();

    // To display heading
    useEffect(() => {
        handleUpdate();
        getCompanyLocations();
        getadvertisement();
    }, [])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: props.userType !== userType["CARWASH-OWNER"] ? "Camps Car Wash" : advertisement_id ? "Edit Advertisement" : "Add New Advertisement", url: props.userType !== userType["CARWASH-OWNER"] ? `/superadmin/${pageURLs.manage_advertisements}/${company_id}` : `/${pageURLs.manage_advertisements}/${company_id}`, showBackButton: true, showMainMenu: true, activeLink: "manage-car-wash" });
    };

    const today = new Date(); // Current browser date
    const defaultStartDate = new Date(today); // Set to today's date
    const defaultEndDate = new Date(today); // Set to today's date

    // file value
    const [currentfilevalue, setCurrentfilevalue] = useState("");
    // Initialize states for formdata
    const [formData, setFormData] = useState({
        advertisement_id: advertisement_id ? advertisement_id :0,
        advertisementtype: 1,
        assignedlocation: 0,
        advertisementname: '',
        startDate:  '',
        endDate: '',
        file: currentfilevalue,
        advertisement_des: '',
    });

    // State to handle errors
    // const [errors, setErrors] = useState<any>({});
    const [errors, setErrors] = useState({
        advertisementtype: "",
        assignedlocation: "",
        advertisementname: "",
        startDate:  "",
        endDate: "",
        file: "",
        advertisement_des: "",
    });

    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});

    const [startDate, setStartDate] = useState<Date | null>(advertisement_id ? defaultStartDate : null);
    const [endDate, setEndDate] = useState<Date | null>(advertisement_id ? defaultEndDate : null);

    const handleDateChange = (setter: React.Dispatch<React.SetStateAction<Date | null>>) => (date: Date | null, name: string) => {
        setter(date);
    
        // Format the date to MM/DD/YY
        const formattedDate = date ? date.toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' }) : null;
    
        // Remove validation error when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            });
        }
    
        const newFormData = {
            ...formData,
            [name]: formattedDate,
        };
        setFormData(newFormData);
    };

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption?.value || '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        const newFormData = {
            ...formData,
            [name]: value,
        };
        setFormData(newFormData);
    };

    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        const newFormData = {
            ...formData,
            [name]: value,
        };

        setFormData(newFormData);
    };

        const fileInputRef = useRef<HTMLInputElement>(null);
        const [advertisementImage, setAdvertisementImage] = useState("");
        const [advertisementImageName, setAdvertisementImageName] = useState("");
        const [AdvertisementImageError, setAdvertisementImageError] = useState("");
        const [advertisementImageUrl, setAdvertisementImageUrl] = useState("");

        const removeadvertisementImage = async () => {
            setAdvertisementImageUrl('');
            setAdvertisementImage('');
            setAdvertisementImageName('');
            formData.file = "";
            // Reset the file input value to allow re-uploading the same file
            if (fileInputRef.current) {
                fileInputRef.current.value = ''; // Setting to an empty string clears the input
            }
        }

        //Upload File
        const handleFileUpload = async (e: any) => {
            setAdvertisementImageError('');
            errors.file = "";
            if (e.target.files && e.target.files[0]) {
                const img = e.target.files[0];
                const fileSizeMB = (img.size / 1048576).toFixed(2);
                if (parseFloat(fileSizeMB) > CUSTOM_ADVERTISEMENT_IMAGE_SIZE) {
                    setLoading(false)
                    setAdvertisementImageError(CommonMessages.custom_advertisement_image_size_validation);
                    return false;
                }
                const isValid = validateImageTypes(img);
                if (isValid) {
                    const folderName = "advertisement/"
                    const randomNumber = Math.floor(Math.random() * 1000000);
                    const extension = img.name.substring(img.name.lastIndexOf("."));
                    const newImageName = randomNumber + extension;
                    setLoading(true);
                    setAdvertisementImage(URL.createObjectURL(img));
                    setAdvertisementImageName(img.name)
                    const request_params = {
                        key: folderName + newImageName
                    }
    
                    const response = (props.userType === userType['CARWASH-OWNER']) ? 
                    await axios.post(CommonApi.CARWASH_OWNER_PRESIGNED_URL, request_params) :
                    await axios.post(CommonApi.PRESIGNED_URL, request_params) ;
                    if (response?.data?.error === 0) {
                        const uploadURL = response.data.data
                        if (uploadURL) {
                            try {
                                // PUT request: upload file to S3
                                const result = await fetch(uploadURL, {
                                    method: "PUT",
                                    body: img,
                                });
                                if (result.status === 200) {
                                    setLoading(false)
                                    setFormData({
                                        ...formData,
                                        'file': folderName + newImageName,
                                    });
                                    setAdvertisementImageUrl(folderName + newImageName);
                                    setCurrentfilevalue(folderName + newImageName);
                                } else {
                                    setLoading(false)
                                }
                            } catch (e) {
                                setLoading(false)
                                console.log(e)
                            }
                        } else {
                            setLoading(false)
                        }
                    } else {
                        setLoading(false)
                        setAdvertisementImageError(response?.data?.message)
                    }
                } else {
                    setAdvertisementImageError("Upload image files with .png or .jpeg extensions only.")
                    setAdvertisementImage('');
                    setAdvertisementImageName('')
                }
            }
        }


        const videofileInputRef = useRef<HTMLInputElement>(null);
        const [advertisementvideo, setAdvertisementVideo] = useState("");
        const [advertisementVideoName, setAdvertisementVideoName] = useState("");
        const [AdvertisementVideoError, setAdvertisementVideoError] = useState("");
        const [advertisementVideoUrl, setAdvertisementVideoUrl] = useState("");

        const removeadvertisementVideo = async () => {
            setAdvertisementVideoUrl('');
            setAdvertisementVideo('');
            setAdvertisementVideoName('');
            formData.file = "";
            // Reset the file input value to allow re-uploading the same file
            if (videofileInputRef.current) {
                videofileInputRef.current.value = ''; // Setting to an empty string clears the input
            }
        }

        const handleVideoFileUpload = (e: any) => {
            handleVideoFileUploadaction(e);
            setAdvertisementVideoUrl('');
            setAdvertisementVideo('');
            // setAdvertisementVideoName('');
            formData.file = "";
            // Reset the file input value to allow re-uploading the same file
            if (videofileInputRef.current) {
                videofileInputRef.current.value = ''; // Setting to an empty string clears the input
            }
        }

        //Upload File
        const handleVideoFileUploadaction = async (e: any) => {
            setAdvertisementVideoError('');
            errors.file = "";
            if (e.target.files && e.target.files[0]) {
                const img = e.target.files[0];
                    const folderName = "advertisement_video/"
                    const randomNumber = Math.floor(Math.random() * 1000000);
                    const extension = img.name.substring(img.name.lastIndexOf("."));
                    const newImageName = randomNumber + extension;
                    setLoading(true)
                    setAdvertisementVideo(URL.createObjectURL(img));
                    setAdvertisementVideoName(img.name)
                    const request_params = {
                        key: folderName + newImageName
                    }
    
                    const response = (props.userType === userType['CARWASH-OWNER']) ? 
                    await axios.post(CommonApi.CARWASH_OWNER_PRESIGNED_URL, request_params) :
                    await axios.post(CommonApi.PRESIGNED_URL, request_params) ;
                    if (response?.data?.error === 0) {
                        const uploadURL = response.data.data
                        if (uploadURL) {
                            try {
                                // PUT request: upload file to S3
                                const result = await fetch(uploadURL, {
                                    method: "PUT",
                                    body: img,
                                });
                                if (result.status === 200) {
                                    setLoading(false)
                                    setFormData({
                                        ...formData,
                                        'file': folderName + newImageName,
                                    });
                                    setAdvertisementVideoUrl(folderName + newImageName);
                                    setCurrentfilevalue(folderName + newImageName);
                                } else {
                                    setLoading(false)
                                }
                            } catch (e) {
                                setLoading(false)
                                console.log(e)
                            }
                        } else {
                            setLoading(false)
                        }
                    } else {
                        setLoading(false)
                        setAdvertisementVideoError(response?.data?.message)
                    }
            }
        }

        useEffect(() => {
            if(formData.advertisementtype === 2)
            {
                setCurrentfilevalue(advertisementImageUrl);
            }
            else if(formData.advertisementtype === 3)
            {
                setCurrentfilevalue(advertisementVideoUrl);
            }
            else
            {
                setCurrentfilevalue("");  
            }
            
        }, [advertisementImageUrl, advertisementVideoUrl, formData.advertisementtype]);
    

    //To validate form on clicking next
    const validateForm = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        try {
            setLoading(true);
            console.log(formData);
            await cwoaddadvertisementSchema.validate(formData, { abortEarly: false });
            const request_params = {
                adType: formData.advertisementtype,
                locationId: formData.assignedlocation,
                adName: formData.advertisementname,
                validFrom: formData.startDate,
                validTo: formData.endDate,
                filePath: currentfilevalue,
                adDescription: formData.advertisement_des
              };
            const edit_request_params = {
                adId:advertisement_id,
                adType: formData.advertisementtype,
                locationId: formData.assignedlocation,
                adName: formData.advertisementname,
                validFrom: formData.startDate,
                validTo: formData.endDate,
                filePath: currentfilevalue,
                adDescription: formData.advertisement_des
            };
            const responseaddadvertisement = (props.userType !== userType['CARWASH-OWNER']) ? 
            (advertisement_id ? await axios.post(AdvertisementApi.MODIFY_ADVERTISEMENT, edit_request_params) :
            await axios.post(AdvertisementApi.CREATE_ADVERTISEMENT, request_params) )
            :
            (advertisement_id ? await axios.post(AdvertisementApi.CARWASH_OWNER_MODIFY_ADVERTISEMENT, edit_request_params) :
             await axios.post(AdvertisementApi.CARWASH_OWNER_CREATE_ADVERTISEMENT, request_params) );
            if (responseaddadvertisement?.data?.error === 0) {
                toaster.toastSuccess("Advertisement added successfully.");
                setErrors({
                    advertisementtype: "",
                    assignedlocation: "",
                    advertisementname: "",
                    startDate:  "",
                    endDate: "",
                    file: "",
                    advertisement_des: "",
                });
                if(props.userType !== userType['CARWASH-OWNER']) {
                    navigate(`/superadmin/${pageURLs.manage_advertisements}/${company_id}`);
                } else {
                    navigate(`/${pageURLs.manage_advertisements}/${company_id}`);
                }
            }
            else {
                toaster.toastError(responseaddadvertisement?.data?.message);
            }
            return true;
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                const errorFields: string[] = [];

                // This is done to get the errors in order
                const fieldOrder = ['advertisementtype', 'assignedlocation', 'advertisementname', 'startDate', 'endDate'];

                error.inner.forEach((err: any) => {
                    if (!newErrors[err.path]) {
                        newErrors[err.path] = err.message;
                        if (!errorFields.includes(err.path)) {
                            errorFields.push(err.path);
                        }
                    }
                });

                setErrors(newErrors);

                // Find the first field with error in the predefined order
                const firstErrorField = fieldOrder.find(field => errorFields.includes(field));
                const element = refs.current[firstErrorField || ''];
                if (element) {
                    element.focus();
                }
            }
            return false
        }
        finally{
            setLoading(false);
        }
    };

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    return (

        <div className='manage-carwash-container manage-subscription-container cwo-advertisement-wrap'>
            <Container>
            {(props.userType !== userType['CARWASH-OWNER']) ?(
                <>
                <Breadcrumb>
                    <Breadcrumb.Item onClick={() => { itemClick(pageURLs.manage_carwash) }}>Manage Car Washes</Breadcrumb.Item>
                    <Breadcrumb.Item onClick={() => { itemClick(`${pageURLs.carwash_locations}/${company_id}`) }}  > Camps Car Wash</Breadcrumb.Item>
                    <Breadcrumb.Item onClick={() => { itemClick(pageURLs.manage_advertisements) }}>Manage Advertisements</Breadcrumb.Item>
                    <Breadcrumb.Item active>Add New Advertisement</Breadcrumb.Item>
                </Breadcrumb>
                <div className="manage-activity-wrap d-flex justify-content-between align-items-center">
                <GenerateLink/>
                    <div className="activity-status d-flex">
                        <CarWashStatus carWashId={1} status={true} companyName={"abc"}/>
                        <div className="stat-group d-flex align-items-center">
                            <p>Subscription Status</p>
                            <Badge className='active'>Active</Badge>
                        </div>

                    </div>
                </div>
                </>
                ):( '')}
                <div className='cw-form-outer-wrap'>
                    <form onSubmit={(e) => validateForm(e)}>
                        <div className="cw-form-container">
                            <div className="form-inner mx-auto">
                                <Row>
                                    <Col md={6}>
                                        <CWSelect
                                            id="advertisementtype"
                                            name="advertisementtype"
                                            options={adtype}
                                            values={adtype.filter(option => option.value === formData.advertisementtype)}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'advertisementtype')}
                                            label="Select Advertisement Type"
                                            errorMessage={errors.advertisementtype && errors.advertisementtype}
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder=""
                                            multi={false}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWSelect
                                            id="assignedlocation"
                                            name="assignedlocation"
                                            options={locations}
                                            values={locations.filter(option => option.value === formData.assignedlocation)}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'assignedlocation')}
                                            label="Select Assign Location"
                                            errorMessage={errors.assignedlocation && errors.assignedlocation}
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Assign Location"
                                            multi={false}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="advertisementname"
                                            name="advertisementname"
                                            type="text"
                                            label="Enter Advertisement Name"
                                            value={formData.advertisementname}
                                            onChange={handleChange}
                                            errorMessage={errors.advertisementname}
                                            maxLength={NAME_MAX_LENGTH}
                                            isMandatory
                                            ref={(el) => (refs.current.advertisementname = el)}
                                        />
                                        <Row>
                                            <Col md={6}>
                                                <div className="form-group date-space">
                                                    <p className="label">Valid From</p>
                                                    <DatePicker
                                                        showIcon
                                                        selected={startDate}
                                                        onChange={date => handleDateChange(setStartDate)(date, 'startDate')}
                                                        placeholderText='MM/DD/YYYY'
                                                        id="startDate"
                                                        name="startDate"
                                                        minDate={new Date()}
                                                    />
                                                </div>
                                                <span className="error-msg">{errors.startDate}</span>
                                            </Col>
                                            <Col md={6}>
                                                <div className="form-group date-space">
                                                    <p className="label">Valid To</p>
                                                    <DatePicker
                                                        showIcon
                                                        selected={endDate}
                                                        onChange={date => handleDateChange(setEndDate)(date, 'endDate')}
                                                        placeholderText='MM/DD/YYYY'
                                                        id="endDate"
                                                        name="endDate"
                                                        minDate= { formData.startDate ? new Date(formData.startDate) : new Date() }
                                                    />
                                                </div>
                                                <span className="error-msg">{errors.endDate}</span>
                                            </Col>
                                        </Row>
                                    </Col>
                                    {formData.advertisementtype === 2 ? (
                                        <Col md={6}>
                                            <div className="file-uploader-wrap">
                                                <div className="file-upload d-flex align-items-center justify-content-center">
                                                    <div className="upload-info text-center">
                                                        <img src={Fileimgicon} alt="" />
                                                        <p className="f-semi">Upload Image</p>
                                                        {advertisementImageName && <span className="file-name">{advertisementImageName}</span>}
                                                    </div>
                                                    <Form.Control
                                                        type="file"
                                                        accept=".jpeg, .png"
                                                        onChange={handleFileUpload}
                                                        className="file-input"
                                                        ref={fileInputRef}
                                                    />
                                                </div>
                                                <span className="upload-note">
                                                    Note: Accepted image formats are .jpeg and .png
                                                </span>
                                            </div>
                                             <span className="error-msg">{errors.file}</span>
                                        </Col>
                                    ) : formData.advertisementtype === 3 ? (
                                        <Col md={6}>
                                            <div className="file-uploader-wrap">
                                                <div className="file-upload d-flex align-items-center justify-content-center">
                                                    <div className="upload-info text-center">
                                                        <img src={Videoimgicon} alt="" />
                                                        <p className="f-semi">Upload Video</p>
                                                        {advertisementVideoName && <span className="file-name">{advertisementVideoName}</span>}
                                                    </div>
                                                    <Form.Control
                                                        type="file"
                                                        accept=".mp4"
                                                        onChange={handleVideoFileUpload}
                                                        className="file-input"
                                                        ref={videofileInputRef}
                                                    />
                                                </div>
                                                <span className="upload-note">
                                                    Note: Accepted video format is .mp4
                                                </span>
                                            </div>
                                            
                                             <span className="error-msg">{errors.file}</span>
                                        </Col>
                                    ) : (
                                        <Col md={6} className='adv-textbox'>
                                            <CWTextarea
                                                id="advertisement_des"
                                                name="advertisement_des"
                                                type="text"
                                                label="Enter Advertisement Description"
                                                maxLength={WASH_DETAIL_MAX_LENGTH}
                                                isMandatory
                                                value={formData.advertisement_des}
                                                errorMessage={errors.advertisement_des}
                                                onChange={handleChange}
                                            />
                                        </Col>
                                    )}
                                </Row>
                                <div className='mt-4'>
                                    {advertisementImage ? (
                                        <div className="preview-logo mx-auto">
                                            <p>Advertisement Preview</p>
                                            <div className="common-cover d-flex align-items-center justify-content-center">
                                                <Button variant="light" className="light-btn close" onClick={removeadvertisementImage}>
                                                    <CloseIcon />
                                                </Button>
                                                <img src={advertisementImage} alt="Car Wash App" height="110px" />
                                            </div>
                                        </div>
                                    ) : (
                                        advertisementImageUrl && (
                                            <div className="preview-logo mx-auto">
                                                <p>Advertisement Preview</p>
                                                <div className="common-cover d-flex align-items-center justify-content-center">
                                                    <Button variant="light" className="light-btn close" onClick={removeadvertisementImage}>
                                                        <CloseIcon />
                                                    </Button>
                                                    <img src={`${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${advertisementImageUrl}`} alt="Car Wash App" height="110px" />
                                                </div>
                                            </div>
                                        )
                                    )}
                                    {AdvertisementImageError && (
                                        <span className="error-msg">{AdvertisementImageError}</span>
                                    )}
                                </div>
                                <div className='mt-4'>
                                    {advertisementvideo ? (
                                                <div className="preview-logo mx-auto">
                                                    <p>Advertisement Preview</p>
                                                    <div className="common-cover d-flex align-items-center justify-content-center">
                                                        <Button variant="light" className="light-btn close" onClick={removeadvertisementVideo}>
                                                            <CloseIcon />
                                                        </Button>
                                                        <video width="200" controls>
                                                            <source src={advertisementvideo}/>
                                                        </video>
                                                        {/* <img src={advertisementvideo} alt="Car Wash App" height="110px" /> */}
                                                    </div>
                                                </div>
                                            ) : (
                                    advertisementVideoUrl && (
                                        <div className="preview-logo mx-auto">
                                            <p>Advertisement Preview</p>
                                            <div className="common-cover d-flex align-items-center justify-content-center">
                                                <Button variant="light" className="light-btn close" onClick={removeadvertisementVideo}>
                                                    <CloseIcon />
                                                </Button>
                                                <video width="200" controls>
                                                    <source src={`${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${advertisementVideoUrl}`}/>
                                                </video>
                                                {/* <img src={`${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${advertisementVideoUrl}`} alt="Car Wash App" height="110px" /> */}
                                            </div>
                                        </div>
                                    )
                                    )}
                                    {AdvertisementVideoError && (
                                        <span className="error-msg">{AdvertisementVideoError}</span>
                                    )}
                                </div>
                                <div className="btn-sec d-flex justify-content-end">
                                    <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => props.userType !== userType["CARWASH-OWNER"] ? navigate(`/superadmin/${pageURLs.manage_advertisements}/${company_id}`) : navigate(`/${pageURLs.manage_advertisements}/${company_id}`)}>Cancel</Button>
                                    <Button title="Save" variant="primary" className="primary-btn" type="submit">Save</Button>
                                </div>
                            </div>
                        </div>
                        
                    </form>
                </div>
            </Container>
            {loading && <SmallLoader />}
        </div >
    )
}

export default AddVideoAdvertisement;
